import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, ShieldCheck, Clock, CheckCircle2, XCircle, SearchCheck, FileDown, Plus, Landmark, FileWarning, Upload } from "lucide-react";
import { ASSETS } from "../mock";
import { CashoutForm, CashoutBadge, downloadReceipt, EMPTY_CASHOUT } from "./admin/AdminCashouts";
import { inputCls, btnPrimary, btnGhost } from "./admin/crm/shared";

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span data-testid="merchant-clock" className="font-semibold text-black/55 tabular-nums">
      {now.toLocaleDateString("es-PA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {now.toLocaleTimeString("es-PA", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </span>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANKS = { BG: "Banco General", Banesco: "Banesco", Banistmo: "Banistmo" };
const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const STATUS = {
  pendiente: { label: "Pendiente de revisión", desc: "Recibimos tu solicitud. Nuestro equipo la revisará en 24 a 48 horas hábiles.", cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: Clock },
  en_revision: { label: "En revisión", desc: "Estamos validando tus datos y documentos. Te avisaremos por correo al terminar.", cls: "bg-blue-50 border-blue-200 text-blue-800", Icon: SearchCheck },
  aprobado: { label: "Aprobada", desc: "¡Tu cuenta RapidPay está activa! Ya puedes solicitar cashouts a tu banco.", cls: "bg-[#0fe814]/10 border-[#0fb013]/30 text-[#0a7d0d]", Icon: CheckCircle2 },
  rechazado: { label: "Rechazada", desc: "Tu solicitud no fue aprobada. Revisa el motivo y contáctanos si tienes dudas.", cls: "bg-[#ff603e]/10 border-[#ff603e]/30 text-[#c2410c]", Icon: XCircle },
};
const KYC_LABEL = { cedula_pasaporte: "Cédula o Pasaporte", aviso_operaciones: "Aviso de Operaciones", registro_publico: "Registro Público", carta_bancaria: "Carta Bancaria", recibo_servicio: "Recibo de Agua o Luz", otro: "Documento adicional" };

const Login = ({ onLogin }) => {
  const [f, setF] = useState({ reference: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const res = await axios.post(`${API}/merchant/login`, { reference: f.reference.trim().toUpperCase(), email: f.email.trim() });
      sessionStorage.setItem("rp_merchant_token", res.data.token);
      onLogin(res.data.token);
    } catch (e2) { setErr(e2.response?.data?.detail || "No se pudo iniciar sesión"); } finally { setBusy(false); }
  };
  return (
    <form data-testid="merchant-login" onSubmit={submit} className="bg-white border border-black/[0.06] rounded-3xl p-6 sm:p-10 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-5">
        <img src="/logos/rapidpay-color.webp" alt="RapidPay" data-testid="merchant-card-logo" className="h-9 w-auto" />
        <div className="w-11 h-11 rounded-2xl bg-black flex items-center justify-center"><ShieldCheck size={18} className="text-[#0fe814]" /></div>
      </div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-[#0fb013]">Portal del comercio</p>
      <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-black">Consulta tu solicitud</h1>
      <p className="mt-2 text-[13px] font-medium text-black/60">Ingresa la referencia que recibiste por correo (ej. RP-A1B2C3) y el correo del representante.</p>
      <label className="block mt-6 text-[11px] font-bold uppercase tracking-wider text-black/45">Referencia</label>
      <input data-testid="merchant-reference" required className={`${inputCls} mt-1 uppercase`} placeholder="RP-XXXXXX" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
      <label className="block mt-4 text-[11px] font-bold uppercase tracking-wider text-black/45">Correo del representante</label>
      <input data-testid="merchant-email" required type="email" className={`${inputCls} mt-1`} placeholder="tu@correo.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      {err && <p data-testid="merchant-login-error" className="mt-3 text-[12px] font-bold text-[#ff603e]">{err}</p>}
      <button data-testid="merchant-login-submit" type="submit" disabled={busy} className="mt-6 w-full inline-flex items-center justify-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] py-3.5 rounded-full transition-colors shadow-[0_5px_16px_rgba(15,232,20,0.35)] disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Ingresar
      </button>
      <p className="mt-4 text-center text-[12px] font-medium text-black/45">¿Aún no tienes cuenta? <Link to="/registro" className="font-bold text-[#0fb013] underline">Regístrate</Link></p>
    </form>
  );
};

export default function MerchantPortal() {
  const [token, setToken] = useState(() => sessionStorage.getItem("rp_merchant_token") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (tk) => {
    setLoading(true);
    try { setData((await axios.get(`${API}/merchant/me`, { headers: { Authorization: `Bearer ${tk}` } })).data); }
    catch { sessionStorage.removeItem("rp_merchant_token"); setToken(""); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (token) load(token); }, [token, load]);

  const logout = () => { sessionStorage.removeItem("rp_merchant_token"); setToken(""); setData(null); };

  const createCashout = async (payload) => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/merchant/cashouts`, payload, { headers });
      setCreating(false); await load(token);
    } catch (e) { setErr(e.response?.data?.detail || "No se pudo enviar la solicitud"); } finally { setBusy(false); }
  };

  const app = data?.application;
  const st = app ? STATUS[app.status] || STATUS.pendiente : null;
  const pendingKyc = (data?.kyc || []).filter((k) => k.verification === "rechazado" && !k.resubmitted_at);

  return (
    <div className="font-montserrat min-h-screen bg-[#fafcf9]">
      <header className="bg-white border-b border-black/[0.06]">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><img src={ASSETS.logo} alt="RapidPay" data-testid="merchant-header-logo" className="h-8 w-auto" /></Link>
          {app && (
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-[12px] font-bold text-black/60">{app.business?.name} · <span className="text-[#0fb013]">{app.reference}</span></span>
              <button data-testid="merchant-logout" onClick={logout} className={btnGhost}><LogOut size={13} /> Salir</button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10">
        {!token && <Login onLogin={setToken} />}
        {token && (loading && !data) && <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#0fb013]" /></div>}
        {app && (
          <div className="space-y-5" data-testid="merchant-dashboard">
            <div data-testid="merchant-status-card" className={`rounded-3xl border p-6 sm:p-8 ${st.cls}`}>
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 shrink-0 rounded-2xl bg-white/70 flex items-center justify-center"><st.Icon size={24} /></span>
                <div className="flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">Estado de tu solicitud · {app.reference}</p>
                  <h1 className="text-2xl sm:text-3xl font-extrabold mt-1">{st.label}</h1>
                  <p className="mt-2 text-[14px] font-medium opacity-90">{st.desc}</p>
                  {app.status === "rechazado" && app.rejection_reason && <p data-testid="merchant-rejection-reason" className="mt-3 text-[13px] font-semibold bg-white/60 rounded-xl px-4 py-2.5"><b>Motivo:</b> {app.rejection_reason}</p>}
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-3 gap-3 text-[12px]">
                {[["Negocio", app.business?.name], ["RUC", app.business?.ruc], ["Representante", app.representative?.full_name], ["Banco (CashOut)", app.payment?.bank], ["Cuenta", app.payment?.account_number ? `${app.payment.account_number} · ${app.payment.account_type === "ahorro" ? "Ahorro" : "Corriente"}` : "—"], ["Registrado", app.created_at ? new Date(app.created_at).toLocaleDateString("es-PA", { dateStyle: "long" }) : "—"]].map(([k, v]) => (
                  <div key={k} className="bg-white/60 rounded-xl px-3.5 py-2.5"><p className="text-[10px] font-bold uppercase tracking-wider opacity-60">{k}</p><p className="font-bold truncate">{v || "—"}</p></div>
                ))}
              </div>
            </div>

            {pendingKyc.length > 0 && (
              <div data-testid="merchant-kyc-pending" className="bg-white border border-[#ffd2c6] rounded-3xl p-6">
                <h2 className="text-[15px] font-extrabold text-black inline-flex items-center gap-2"><FileWarning size={18} className="text-[#ff603e]" /> Documentos que debes volver a enviar</h2>
                <div className="mt-3 space-y-2">
                  {pendingKyc.map((k) => (
                    <div key={k.id} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-[#fff4f1] rounded-xl px-4 py-3">
                      <div className="flex-1"><p className="text-[13px] font-bold text-black">{KYC_LABEL[k.category] || k.category}</p>{k.comment && <p className="text-[12px] text-black/60">Motivo: {k.comment}</p>}</div>
                      {k.resubmit_token && <Link data-testid={`merchant-resubmit-${k.id}`} to={`/reenvio/${k.resubmit_token}`} className={btnPrimary}><Upload size={13} /> Subir documento corregido</Link>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white border border-black/[0.06] rounded-3xl p-6" data-testid="merchant-cashouts">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <h2 className="text-[15px] font-extrabold text-black inline-flex items-center gap-2 flex-1"><Landmark size={18} className="text-[#0fb013]" /> Solicitudes de Cashout</h2>
                {app.status === "aprobado" && !creating && <button data-testid="merchant-cashout-new" onClick={() => setCreating(true)} className={btnPrimary}><Plus size={13} /> Solicitar cashout</button>}
              </div>
              {app.status !== "aprobado" && <p className="mt-2 text-[12px] font-medium text-black/50">Podrás solicitar cashouts cuando tu solicitud sea aprobada.</p>}
              {data.balance && app.status === "aprobado" && (
                <div data-testid="merchant-balance" className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[["Ventas", data.balance.ventas], ["Comisiones", data.balance.comisiones], ["Cashouts", data.balance.cashouts_aprobados + data.balance.cashouts_pendientes], ["Disponible para cashout", data.balance.disponible]].map(([k, v], i) => (
                    <div key={k} className={`rounded-xl px-3.5 py-2.5 border ${i === 3 ? "bg-black text-white border-black" : "bg-[#fafcf9] border-black/[0.06]"}`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${i === 3 ? "text-white/70" : "text-black/40"}`}>{k}</p>
                      <p data-testid={i === 3 ? "merchant-balance-disponible" : undefined} className="text-[15px] font-extrabold">{money(v)}</p>
                    </div>
                  ))}
                </div>
              )}
              {creating && (
                <div className="mt-4 border-t border-black/[0.06] pt-4">
                  <CashoutForm initial={{ ...EMPTY_CASHOUT, store: app.business?.name || "", client_id: app.reference, phone: app.representative?.phone || "", beneficiary: app.representative?.full_name || "", email: app.representative?.email || "", account: app.payment?.account_number || "", account_type: app.payment?.account_type || "ahorro" }} onSubmit={createCashout} onCancel={() => setCreating(false)} submitting={busy} error={err} testPrefix="m-co" />
                </div>
              )}
              {(data.cashouts || []).length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead><tr className="text-left text-[10px] uppercase tracking-wider text-black/45 border-b border-black/[0.06]">{["Fecha", "Banco · Cuenta", "ID TRX", "Monto", "Estado", "Recibo"].map((h) => <th key={h} className="px-3 py-2 font-bold">{h}</th>)}</tr></thead>
                    <tbody>
                      {data.cashouts.map((c) => (
                        <tr key={c.id} data-testid={`merchant-cashout-${c.id}`} className="border-b border-black/[0.04]">
                          <td className="px-3 py-3 font-semibold whitespace-nowrap">{c.date}</td>
                          <td className="px-3 py-3">{BANKS[c.bank]} · {c.account}</td>
                          <td className="px-3 py-3 font-mono text-[11px]">{c.trx_id}</td>
                          <td className="px-3 py-3 font-extrabold text-[13px]">{money(c.amount)}</td>
                          <td className="px-3 py-3"><CashoutBadge status={c.status} />{c.status === "rechazado" && c.reject_reason && <p className="mt-1 text-[10px] text-black/50">{c.reject_reason}</p>}</td>
                          <td className="px-3 py-3">{c.status === "aprobado" && <button data-testid={`merchant-receipt-${c.id}`} onClick={() => downloadReceipt(`${API}/merchant/cashouts/${c.id}/receipt.pdf`, headers, `recibo_${c.receipt?.trx_number}.pdf`)} className={btnGhost}><FileDown size={12} /> {c.receipt?.trx_number}</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <footer data-testid="merchant-footer" className="pb-8 flex flex-col items-center gap-3 text-[11px]">
        <LiveClock />
        <div className="flex items-center gap-2 text-black/40 font-semibold">
          <span>Seguridad certificada por</span>
          <img src="/logos/entrust.webp" alt="Entrust" data-testid="merchant-entrust-logo" className="h-6 w-auto" />
        </div>
      </footer>
    </div>
  );
}
